import { faClockRotateLeft, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Period } from '@spike/model';
import { AppointmentOrderItem } from '@spike/order-model';
import Pet from '@spike/pet-model';
import clsx from 'clsx';
import { ActivityTimeline } from 'components/Activities/Timeline/ActivityTimeline';
import { ActivityTimelineProvider, useActivityTimeline } from 'components/Activities/Timeline/ActivityTimelineProvider';
import styles from 'components/OrderDetail/Tabs/Activities/OrderDetailActivitiesTab.module.scss';
import Button from 'components/UI/V3/Button/Button';
import { PetProfileCard } from 'components/UI/V3/PetProfileCard/PetProfileCard';
import { PetTagTabs } from 'components/UI/V3/PetTag/Tabs/PetTagTabs';
import moment from 'moment-timezone';
import React from 'react';

const buildAppointmentDateRange = (appointmentItems: Array<AppointmentOrderItem>): Period | undefined => {
    let from;
    let to;

    for (const item of appointmentItems) {
        const appointmentFrom = item.appointment.duration.from;
        const appointmentTo = item.appointment.duration.to;

        if (!from || appointmentFrom.isBefore(from)) {
            from = appointmentFrom;
        }

        if (!to || appointmentTo.isAfter(to)) {
            to = appointmentTo;
        }
    }

    if (!from || !to) {
        return undefined;
    }

    return { from, to };
};

const getAppointmentActivityDates = (appointmentItem: AppointmentOrderItem): Array<string> => {
    const businessAreaId = appointmentItem.appointment.services[0]?.businessArea?.id;
    const startDay = appointmentItem.appointment.duration.from.clone().startOf('day');

    if (businessAreaId !== 'boarding') {
        return [startDay.format('YYYY-MM-DD')];
    }

    const endDay = appointmentItem.appointment.duration.to.clone().startOf('day');
    const dates: Array<string> = [];
    const currentDay = startDay.clone();

    while (currentDay.isSameOrBefore(endDay, 'day')) {
        dates.push(currentDay.format('YYYY-MM-DD'));
        currentDay.add(1, 'day');
    }

    return dates;
};

const getAppointmentActivityTimeRanges = (appointmentItem: AppointmentOrderItem): Record<string, Array<Period>> => {
    const businessAreaId = appointmentItem.appointment.services[0]?.businessArea?.id;
    const appointmentFrom = appointmentItem.appointment.duration.from.clone();
    const appointmentTo = appointmentItem.appointment.duration.to.clone();
    const timeRanges: Record<string, Array<Period>> = {};

    if (businessAreaId !== 'boarding') {
        timeRanges[appointmentFrom.format('YYYY-MM-DD')] = [
            {
                from: appointmentFrom,
                to: appointmentTo
            }
        ];

        return timeRanges;
    }

    const currentDay = appointmentFrom.clone().startOf('day');
    const lastDay = appointmentTo.clone().startOf('day');

    while (currentDay.isSameOrBefore(lastDay, 'day')) {
        const dateKey = currentDay.format('YYYY-MM-DD');
        const from = currentDay.isSame(appointmentFrom, 'day')
            ? appointmentFrom.clone()
            : currentDay.clone().startOf('day');
        const to = currentDay.isSame(appointmentTo, 'day') ? appointmentTo.clone() : currentDay.clone().endOf('day');

        timeRanges[dateKey] = [
            {
                from,
                to
            }
        ];

        currentDay.add(1, 'day');
    }

    return timeRanges;
};

interface OrderDetailActivitiesTabProps {
    pets: Array<Pet>;
    appointmentItems: Array<AppointmentOrderItem>;
    bookingId: number;
    customerId: number;
    selectedPet?: Pet;
    onSelectedPetChange: React.Dispatch<React.SetStateAction<Pet | undefined>>;
    setShowActivityHistory: React.Dispatch<React.SetStateAction<boolean>>;
    className?: string;
    dateLimits?: Period;
    zIndex?: number;
    readOnly?: boolean;
}

export const OrderDetailActivitiesTab: React.FC<OrderDetailActivitiesTabProps> = ({
    pets,
    appointmentItems,
    bookingId,
    customerId,
    selectedPet,
    onSelectedPetChange,
    setShowActivityHistory,
    dateLimits,
    className,
    zIndex,
    readOnly
}) => {
    const activePet = selectedPet ?? pets[0];

    const activePetAppointmentItems = React.useMemo(() => {
        if (!activePet) {
            return [];
        }

        return appointmentItems.filter(item => item.appointment.pet.id === activePet.id);
    }, [activePet, appointmentItems]);

    const createActivityDateLimits = React.useMemo(() => {
        return buildAppointmentDateRange(activePetAppointmentItems) ?? dateLimits;
    }, [activePetAppointmentItems, dateLimits]);

    const createActivityAllowedDates = React.useMemo(() => {
        const allowedDates = activePetAppointmentItems.reduce<Array<string>>((dates, appointmentItem) => {
            getAppointmentActivityDates(appointmentItem).forEach(date => {
                if (!dates.includes(date)) {
                    dates.push(date);
                }
            });

            return dates;
        }, []);

        return allowedDates.length > 0
            ? allowedDates.sort((left, right) => moment(left).diff(moment(right)))
            : undefined;
    }, [activePetAppointmentItems]);

    const createActivityDateTimeRanges = React.useMemo(() => {
        if (activePetAppointmentItems.length === 0) {
            return undefined;
        }

        return activePetAppointmentItems.reduce<Record<string, Array<Period>>>((acc, appointmentItem) => {
            const nextRanges = getAppointmentActivityTimeRanges(appointmentItem);

            Object.entries(nextRanges).forEach(([date, ranges]) => {
                acc[date] = [...(acc[date] ?? []), ...ranges];
            });

            return acc;
        }, {});
    }, [activePetAppointmentItems]);

    React.useEffect(() => {
        if (!selectedPet && pets[0]) {
            onSelectedPetChange(pets[0]);
        }
    }, [selectedPet, pets, onSelectedPetChange]);

    if (!activePet) {
        return null;
    }

    return (
        <ActivityTimelineProvider
            pets={pets}
            pet={activePet}
            bookingId={bookingId}
            customerId={customerId}
            createActivityDateLimits={createActivityDateLimits}
            createActivityAllowedDates={createActivityAllowedDates}
            createActivityDateTimeRanges={createActivityDateTimeRanges}
            zIndex={zIndex}
        >
            <div className={clsx(styles.root, className)}>
                <PetTagTabs
                    pets={pets}
                    value={activePet}
                    onChange={onSelectedPetChange}
                    className={styles.tabs}
                />

                <PetProfileCard
                    pet={activePet}
                    className={styles.petProfileCard}
                    nameAction={
                        <button
                            type="button"
                            className={styles.activityHistoryButton}
                            onClick={() => setShowActivityHistory(true)}
                        >
                            <FontAwesomeIcon icon={faClockRotateLeft} />
                            Activity History
                        </button>
                    }
                />

                <ActivityTabContent readOnly={readOnly} />
            </div>
        </ActivityTimelineProvider>
    );
};

const ActivityTabContent: React.FC<{ readOnly?: boolean }> = ({ readOnly }) => {
    const { openActivityForm } = useActivityTimeline();
    const isReadOnly = readOnly ?? false;

    return (
        <ActivityTimeline
            infiniteScrollTrigger={document.querySelector('#scrollable-content')}
            readOnly={readOnly}
            classNames={{
                root: styles.timeline
            }}
        >
            <div className={styles.header}>
                <p className={styles.title}>Activities</p>

                <Button
                    variant="green"
                    label="Add Activity"
                    leftIcon={faPlus}
                    onClick={() => !isReadOnly && openActivityForm()}
                    disabled={isReadOnly}
                />
            </div>
        </ActivityTimeline>
    );
};

export default OrderDetailActivitiesTab;
